import { useEffect, useRef, useCallback } from "react";
import useModalActions from "./useModalActions";
import PersonalAccount from "../store/PersonalAccount";
import { MODAL_TYPES } from "../utils/emitter";

/**
 * Хук для управления PWA попапом с инструкциями по добавлению на главный экран
 * @returns {Object} объект с методом openPwaModal
 */
export default function usePwaPopup() {
  const { openModal } = useModalActions();
  const timerRef = useRef(null);
  const shownGameIdsRef = useRef(new Set());

  // Cleanup таймера при размонтировании
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  /**
   * Проверяет, нужно ли показывать PWA попап для данной игры
   * @param {number} gameId - ID игры
   * @returns {boolean} true если ПОРА показать попап, false если не нужно
   */
  const shouldShowPwaPopup = useCallback((gameId) => {
    // некорректный gameId — не показываем
    if (!gameId) return false;

    // уже показывали попап в рамках жизни этого хука
    if (shownGameIdsRef.current.has(gameId)) {
      return false;
    }

    // если в сторе уже зафиксировано, что попап показывали — тоже не показываем
    if (PersonalAccount.hasViewedPwaInstruction(gameId)) {
      shownGameIdsRef.current.add(gameId);
      return false;
    }

    // помечаем, что в этом сеансе уже собираемся показать попап
    shownGameIdsRef.current.add(gameId);
    return true;
  }, []);

  /**
   * Показывает PWA попап для данной игры
   * @param {number} gameId - ID игры
   * @returns {boolean} true если попап был показан
   */
  const showPwaPopup = useCallback((gameId) => {
    if (shouldShowPwaPopup(gameId)) {
      openModal(MODAL_TYPES.ADD_MAIN_SCREEN);
      PersonalAccount.addGameIdsByPwaInstructionPopup(gameId);
      return true;
    }
    return false;
  }, [shouldShowPwaPopup]);

  /**
   * Открывает PWA модалку с инструкциями (опционально с задержкой)
   * @param {number} gameId - ID игры
   * @param {boolean} [startTimer=false] - если true, показывает попап через 20 секунд
   * @returns {boolean|void} true/false если попап показан сразу, undefined если установлен таймер
   */
  const openPwaModal = useCallback((gameId, startTimer = false) => {
    // Очищаем предыдущий таймер если был
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Показываем попап с задержкой
    if (startTimer) {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        showPwaPopup(gameId);
      }, 20000); // 20 секунд
      return;
    }

    // Показываем попап сразу
    return showPwaPopup(gameId);
  }, []);

  return {
    openPwaModal,
  };
}
